import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@material-ui/core";
import { CheckCircle, ErrorOutline, Refresh } from "@material-ui/icons";

import api from "../../services/api";

// Teste de conexao do servidor com a Meta e a Gupshup, sem precisar de SSH.
// Mostra cada IP separado (em 21/10/2026 a Work perdia conexao com ALGUNS IPs
// da Gupshup) e as falhas de conexao recentes, que um teste isolado nao ve.

const useStyles = makeStyles(theme => ({
	section: { marginBottom: theme.spacing(2.5) },
	sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1) },
	card: {
		border: "1px solid rgba(0,0,0,0.12)",
		borderRadius: 8,
		padding: theme.spacing(1.25, 1.5),
		marginBottom: theme.spacing(1),
	},
	row: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, lineHeight: 1.6 },
	ok: { color: "#2e7d32", fontSize: 18 },
	bad: { color: "#c62828", fontSize: 18 },
	muted: { color: "rgba(0,0,0,0.6)", fontSize: 12 },
	host: { fontWeight: 600, fontSize: 13 },
	ip: { fontFamily: "monospace", fontSize: 12.5, minWidth: 120 },
	warn: { color: "#c62828", fontSize: 12.5, marginTop: 4 },
	loading: { display: "flex", alignItems: "center", gap: 12, padding: theme.spacing(3, 0) },
}));

const formatTime = value => {
	if (!value) return "";
	try {
		return new Date(value).toLocaleString("pt-BR");
	} catch (_error) {
		return value;
	}
};

const errorText = code => {
	const map = {
		TEMPO_ESGOTADO: "não conectou (tempo esgotado)",
		UND_ERR_CONNECT_TIMEOUT: "não conectou (tempo esgotado)",
		ECONNREFUSED: "conexão recusada",
		ENETUNREACH: "rede inalcançável",
		EHOSTUNREACH: "servidor inalcançável",
		ENOTFOUND: "nome não encontrado (DNS)",
		EAI_AGAIN: "DNS não respondeu",
		TimeoutError: "sem resposta a tempo",
	};
	return map[code] || code || "falhou";
};

const Status = ({ ok, classes }) =>
	ok ? <CheckCircle className={classes.ok} /> : <ErrorOutline className={classes.bad} />;

const ConnectivityTestDialog = ({ open, onClose }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [report, setReport] = useState(null);
	const [error, setError] = useState("");

	const runTest = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const { data } = await api.get("/whatsapp/connectivity-test");
			setReport(data);
		} catch (err) {
			setError(
				String(err?.response?.data?.error || err?.message || "").trim() ||
					"Não foi possível executar o teste."
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (open) runTest();
	}, [open, runTest]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Teste de conexão com a Meta e a Gupshup</DialogTitle>
			<DialogContent dividers>
				{loading && (
					<div className={classes.loading}>
						<CircularProgress size={24} />
						<Typography variant="body2">
							Testando a partir do servidor... pode levar alguns segundos.
						</Typography>
					</div>
				)}
				{!loading && error && (
					<Typography variant="body2" className={classes.warn}>
						{error}
					</Typography>
				)}
				{!loading && report && (
					<>
						<Typography variant="caption" className={classes.muted} display="block" gutterBottom>
							Testado em {formatTime(report.testedAt)}
							{report.cached ? " (resultado de agora há pouco, repetido para não sobrecarregar os provedores)" : ""}.
							O teste é feito pelo servidor do Altchat, que é quem fala com a Meta e a Gupshup.
						</Typography>

						<div className={classes.section}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>
								Servidores dos provedores
							</Typography>
							{report.providers.map(provider => (
								<div key={provider.key} className={classes.card}>
									<Typography variant="subtitle2">
										{provider.label}
										<span className={classes.muted}>
											{" "}
											· {provider.connections} conexão(ões) desta empresa
										</span>
									</Typography>
									{provider.hosts.map(host => (
										<div key={host.host} style={{ marginTop: 6 }}>
											<div className={classes.row}>
												<Status ok={host.https.ok} classes={classes} />
												<span className={classes.host}>{host.host}</span>
												<span className={classes.muted}>
													{host.https.ok
														? `respondeu em ${host.https.ms} ms`
														: errorText(host.https.error)}
												</span>
											</div>
											{host.dnsError && (
												<div className={classes.warn}>DNS: {errorText(host.dnsError)}</div>
											)}
											{host.ips.map(ip => (
												<div key={ip.ip} className={classes.row} style={{ paddingLeft: 24 }}>
													<Status ok={ip.ok} classes={classes} />
													<span className={classes.ip}>{ip.ip}</span>
													<span className={classes.muted}>
														{ip.ok ? `porta 443 abriu em ${ip.ms} ms` : errorText(ip.error)}
													</span>
												</div>
											))}
										</div>
									))}
									{provider.failures.last24h > 0 && (
										<div className={classes.warn}>
											{provider.failures.last24h} falha(s) de conexão nas últimas 24h (
											{provider.failures.lastHour} na última hora
											{provider.failures.gaveUpLast24h
												? `; ${provider.failures.gaveUpLast24h} sem sucesso nem depois das novas tentativas`
												: ""}
											). Última: {formatTime(provider.failures.last?.at)}, em{" "}
											{provider.failures.last?.purpose}.
										</div>
									)}
									{provider.failures.last24h === 0 && provider.key === "gupshup" && (
										<div className={classes.muted} style={{ marginTop: 4 }}>
											Nenhuma falha de conexão registrada desde {formatTime(provider.failures.since)}.
										</div>
									)}
								</div>
							))}
						</div>

						<div className={classes.section}>
							<Typography variant="subtitle2" className={classes.sectionTitle}>
								Conexões oficiais desta empresa
							</Typography>
							{!report.connections.length && (
								<Typography variant="body2" className={classes.muted}>
									Nenhuma conexão da API oficial (Meta direta ou Gupshup).
								</Typography>
							)}
							{report.connections.map(connection => (
								<div key={connection.id} className={classes.card}>
									<div className={classes.row}>
										<Status ok={connection.ok} classes={classes} />
										<span className={classes.host}>
											#{connection.id} {connection.name}
										</span>
										<span className={classes.muted}>
											({connection.connectionType === "gupshup" ? "Gupshup" : "Meta direta"})
										</span>
									</div>
									<div className={classes.muted} style={{ paddingLeft: 24 }}>
										{connection.ok
											? `Respondeu em ${connection.ms} ms: ${connection.templates} template(s), ${connection.approvedTemplates} aprovado(s).`
											: connection.error}
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button startIcon={<Refresh />} onClick={runTest} disabled={loading}>
					Testar de novo
				</Button>
				<Button onClick={onClose} color="primary" variant="contained">
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ConnectivityTestDialog;
